import type { Polygon } from "geojson";

/** Explicit opt-in; synthetic data never enters the live API cache. */
export const riskPictureDemoEnabled = (): boolean =>
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("riskPictureDemo") === "true";

const area = (west: number, south: number): Polygon => ({
  type: "Polygon",
  coordinates: [
    [
      [west, south],
      [west + 0.7, south],
      [west + 0.7, south + 0.5],
      [west, south + 0.5],
      [west, south],
    ],
  ],
});

/** Anchor once on mount so countdowns decrease rather than resetting every minute. */
export const buildRiskPictureDemo = (anchor: Date) => {
  const at = (minutes: number) =>
    new Date(anchor.getTime() + minutes * 60_000).toISOString();
  const scenarios = [
    {
      id: "demo-active-red",
      phenomenon: "wind",
      color: "red",
      start: -60,
      end: 90,
      west: 4.3,
      south: 52.1,
    },
    // Same area as the active red warning: guaranteed overlap at every zoom level.
    {
      id: "demo-overlapping-rain",
      phenomenon: "rain",
      color: "orange",
      start: -30,
      end: 120,
      west: 4.3,
      south: 52.1,
      headline: "DEMO — Overlappende regenwaarschuwing (cluster)",
    },
    {
      id: "demo-overlapping-fog",
      phenomenon: "fog",
      color: "yellow",
      start: -15,
      end: 150,
      west: 4.3,
      south: 52.1,
      headline: "DEMO — Overlappende mistwaarschuwing (cluster)",
    },
    {
      id: "demo-upcoming-orange",
      phenomenon: "rain",
      color: "orange",
      start: 140,
      end: 360,
      west: 5.0,
      south: 51.8,
    },
    {
      id: "demo-upcoming-red",
      phenomenon: "rain",
      color: "red",
      start: 155,
      end: 330,
      west: 5.2,
      south: 52.0,
    },
    {
      id: "demo-short-gap",
      phenomenon: "thunderstorm",
      color: "yellow",
      start: 5,
      end: 180,
      west: 5.8,
      south: 52.5,
    },
    {
      id: "demo-elapsed-active",
      phenomenon: "fog",
      color: "orange",
      start: -120,
      end: -30,
      west: 4.6,
      south: 52.8,
      headline: "DEMO — Eindtijd verstreken, status active: blijft zichtbaar",
    },
    {
      id: "demo-expired-part",
      phenomenon: "snow",
      color: "yellow",
      start: -120,
      end: -30,
      west: 5.6,
      south: 52.8,
      partStatus: "expired",
      headline: "DEMO — Part expired: niet zichtbaar",
    },
    {
      id: "demo-expired-set",
      phenomenon: "road-ice",
      color: "yellow",
      start: -120,
      end: -30,
      west: 5.3,
      south: 51.4,
      setStatus: "expired",
      headline: "DEMO — Warning set expired: niet zichtbaar",
    },
    // +36h always lands on a later local day than the anchor, whatever the load time.
    {
      id: "demo-chance-later-day",
      phenomenon: "wind",
      color: "orange",
      start: 36 * 60,
      end: 44 * 60,
      west: 6.0,
      south: 53.0,
      warningType: "potentially-dangerous-weather",
      probabilityRange: "30-60",
      headline: "DEMO — Kans op zware windstoten (andere dag)",
    },
  ];
  return {
    metadata: { generated_at: at(0), producer: { name: "DEMO" } },
    warnings: scenarios.map(
      ({
        id,
        phenomenon,
        color,
        start,
        end,
        west,
        south,
        headline,
        setStatus,
        partStatus,
        warningType,
        probabilityRange,
      }) => ({
        warning_set_uuid: `${id}-set`,
        phenomenon,
        warning_type: warningType ?? "warning",
        status: setStatus ?? "active",
        operational_status: "Actual",
        texts: {
          nl: {
            headline: headline ?? `DEMO — ${phenomenon} (${color})`,
            description:
              "Synthetische waarschuwing voor het testen van kleuren en de tijdlijn. Geen echte waarschuwing.",
          },
        },
        parts: [
          {
            part_uuid: id,
            status: partStatus ?? "active",
            color,
            onset: at(start),
            expires: at(end),
            ...(probabilityRange ? { probability_percentage_range: probabilityRange } : {}),
            area: area(west, south),
            area_description: "DEMO — testgebied",
          },
        ],
      }),
    ),
  };
};
